/**
 * app.js — PFS Get Verified: flow controller
 * -------------------------------------------
 * Steps: STATE -> TYPE -> FORM -> REVIEW -> SUBMITTED
 *
 * This file only knows HOW to render/validate/submit a step. It never
 * hard-codes a state name, tier name, or field id — all of that comes
 * from config.js (window.PFS_CONFIG). If you're changing WHAT the flow
 * asks for, edit config.js instead of this file.
 */

(function () {
  "use strict";

  const CFG = window.PFS_CONFIG;

  // Where the serverless intake endpoint lives. See backend/submit-application.js
  // and docs/ARCHITECTURE.md for what this endpoint does and why it exists
  // (the browser can never hold an Admin API token, so it can't write to
  // Shopify directly).
  const SUBMIT_ENDPOINT = "https://pfs-get-verified-intake.profibersystems.workers.dev";

  const STEPS = ["state", "type", "form", "review", "submitted"];

  // All the answers collected so far. Field values keyed by field id.
  const state = {
    step: "state",
    selectedState: null,
    businessType: null, // "carpet" | "rug" | "both"
    fieldValues: {},
    files: {}, // fieldId -> File object
  };

  const root = document.getElementById("app");

  // -----------------------------------------------------------------
  // Rendering
  // -----------------------------------------------------------------

  function render() {
    root.innerHTML = "";
    switch (state.step) {
      case "state":
        root.appendChild(renderStateStep());
        break;
      case "out_of_territory":
        root.appendChild(renderOutOfTerritoryStep());
        break;
      case "type":
        root.appendChild(renderTypeStep());
        break;
      case "form":
        root.appendChild(renderFormStep());
        break;
      case "review":
        root.appendChild(renderReviewStep());
        break;
      case "submitted":
        root.appendChild(renderSubmittedStep());
        break;
      default:
        throw new Error(`Unknown step: ${state.step}`);
    }
    updateProgress();
  }

  function updateProgress() {
    const bar = document.getElementById("progress");
    if (!bar) return;
    const idx = STEPS.indexOf(state.step === "out_of_territory" ? "state" : state.step);
    const pct = idx < 0 ? 0 : (idx / (STEPS.length - 1)) * 100;
    bar.style.width = `${pct}%`;
  }

  // --- Step 1: state selector -----------------------------------------

  function renderStateStep() {
    const el = el_(`
      <section class="step" data-step="state">
        <p class="eyebrow">Step 1 of 4</p>
        <h1>Where's your business based?</h1>
        <p class="lede">PFS distributes exclusively within our western territory. Tell us your state and we'll take you to the right place.</p>
        <label class="field-label" for="state-select">State</label>
        <select id="state-select" class="input">
          <option value="" disabled selected>Choose your state</option>
          ${CFG.TERRITORY_STATES.map((s) => `<option value="${s.code}">${s.name}</option>`).join("")}
          <option value="__outside__">My state isn't listed</option>
        </select>
        <button class="btn btn-primary" id="state-continue" disabled>Continue</button>
      </section>
    `);

    const select = el.querySelector("#state-select");
    const btn = el.querySelector("#state-continue");
    select.addEventListener("change", () => {
      btn.disabled = !select.value;
    });
    btn.addEventListener("click", () => {
      if (select.value === "__outside__") {
        state.selectedState = null;
        goTo("out_of_territory");
        return;
      }
      state.selectedState = select.value;
      goTo("type");
    });
    return el;
  }

  function renderOutOfTerritoryStep() {
    const el = el_(`
      <section class="step" data-step="out_of_territory">
        <p class="eyebrow">Outside PFS territory</p>
        <h1>Let's get you to the right place.</h1>
        <p class="lede">PFS distributes exclusively in the western US. For your region, ChemMax's manufacturer channel is the right place to purchase.</p>
        <a class="btn btn-primary" href="${CFG.OUT_OF_TERRITORY_REDIRECT_URL}" target="_top">Continue to ChemMax</a>
        <button class="btn btn-secondary" id="back-to-state">Back</button>
      </section>
    `);
    el.querySelector("#back-to-state").addEventListener("click", () => goTo("state"));
    return el;
  }

  // --- Step 2: business type -------------------------------------------

  function renderTypeStep() {
    const entries = Object.entries(CFG.BUSINESS_TYPES);
    const el = el_(`
      <section class="step" data-step="type">
        <p class="eyebrow">Step 2 of 4</p>
        <h1>What does your operation do?</h1>
        <p class="lede">This determines which membership tier and application fields apply to you.</p>
        <div class="option-grid" id="type-options">
          ${entries
            .map(
              ([key, def]) => `
            <button class="option-card" type="button" data-type="${key}">
              <span class="option-title">${def.label}</span>
              <span class="option-desc">${def.description}</span>
              <span class="option-tiers">${def.tiers.map((t) => CFG.TIERS[t].name).join(" · ")}</span>
            </button>
          `
            )
            .join("")}
        </div>
        <button class="btn btn-secondary" id="back-to-state-2">Back</button>
      </section>
    `);

    el.querySelectorAll(".option-card").forEach((card) => {
      card.addEventListener("click", () => {
        state.businessType = card.getAttribute("data-type");
        goTo("form");
      });
    });
    el.querySelector("#back-to-state-2").addEventListener("click", () => goTo("state"));
    return el;
  }

  // --- Step 3: branching form -------------------------------------------

  function renderFormStep() {
    const typeDef = CFG.BUSINESS_TYPES[state.businessType];
    const sections = typeDef.sections.map((key) => ({ key, ...CFG.FORM_SECTIONS[key] }));
    const tierNames = typeDef.tiers.map((t) => CFG.TIERS[t].name).join(" or ");

    const el = el_(`
      <section class="step" data-step="form">
        <p class="eyebrow">Step 3 of 4</p>
        <h1>Tell us about your business</h1>
        <p class="lede">Based on your answer, you'll be reviewed for ${tierNames}. Final tier is confirmed after manual review.</p>
        <form id="verification-form" novalidate></form>
        <div class="form-actions">
          <button class="btn btn-secondary" id="back-to-type">Back</button>
          <button class="btn btn-primary" id="form-continue">Review application</button>
        </div>
      </section>
    `);

    const formEl = el.querySelector("#verification-form");
    sections.forEach((section) => {
      formEl.appendChild(renderSection(section));
    });

    el.querySelector("#back-to-type").addEventListener("click", () => goTo("type"));
    el.querySelector("#form-continue").addEventListener("click", (e) => {
      e.preventDefault();
      if (validateForm(sections)) goTo("review");
    });

    return el;
  }

  function renderSection(section) {
    const wrap = el_(`<fieldset class="form-section"><legend>${section.title}</legend></fieldset>`);
    section.fields.forEach((field) => {
      wrap.appendChild(renderField(field));
    });
    return wrap;
  }

  function renderField(field) {
    const value = state.fieldValues[field.id] || "";
    const wrap = el_(`
      <div class="field" data-field="${field.id}">
        <label class="field-label" for="${field.id}">${field.label}${field.required ? " *" : ""}</label>
      </div>
    `);

    let input;
    if (field.type === "textarea") {
      input = el_(`<textarea id="${field.id}" class="input" rows="3">${value}</textarea>`);
    } else if (field.type === "file") {
      input = el_(`<input id="${field.id}" class="input" type="file" ${field.multiple ? "multiple" : ""} />`);
    } else {
      input = el_(`<input id="${field.id}" class="input" type="${field.type}" value="${escapeAttr(value)}" />`);
    }

    input.addEventListener("input", () => {
      if (field.type !== "file") state.fieldValues[field.id] = input.value;
    });
    if (field.type === "file") {
      input.addEventListener("change", () => {
        // Always store as an array internally, even for single-file
        // fields — buildPayload and the review step both expect this
        // shape uniformly, so multiple/required file fields work the
        // same way as any other file field.
        state.files[field.id] = Array.from(input.files || []);
      });
    }

    wrap.appendChild(input);
    wrap.appendChild(el_(`<p class="field-error" id="error-${field.id}"></p>`));
    return wrap;
  }

  function validateForm(sections) {
    let valid = true;
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        const errorEl = document.getElementById(`error-${field.id}`);
        const hasValue =
          field.type === "file"
            ? (state.files[field.id] || []).length > 0
            : !!(state.fieldValues[field.id] || "").trim();
        if (field.required && !hasValue) {
          valid = false;
          if (errorEl) errorEl.textContent = "Required";
        } else if (errorEl) {
          errorEl.textContent = "";
        }
      });
    });
    return valid;
  }

  // --- Step 4: review ----------------------------------------------------

  function renderReviewStep() {
    const typeDef = CFG.BUSINESS_TYPES[state.businessType];
    const sections = typeDef.sections.map((key) => ({ key, ...CFG.FORM_SECTIONS[key] }));

    const summaryHtml = sections
      .map(
        (section) => `
        <div class="review-section">
          <h3>${section.title}</h3>
          <dl>
            ${section.fields
              .map((f) => {
                const display =
                  f.type === "file"
                    ? (state.files[f.id] || []).length > 0
                      ? `${state.files[f.id].length} file${state.files[f.id].length > 1 ? "s" : ""}: ${state.files[f.id].map((file) => file.name).join(", ")}`
                      : "—"
                    : state.fieldValues[f.id] || "—";
                return `<dt>${f.label}</dt><dd>${escapeHtml(display)}</dd>`;
              })
              .join("")}
          </dl>
        </div>`
      )
      .join("");

    const el = el_(`
      <section class="step" data-step="review">
        <p class="eyebrow">Step 4 of 4</p>
        <h1>Review your application</h1>
        <p class="lede">Every application is reviewed by a person before you're granted access. You'll hear back by email.</p>
        ${summaryHtml}
        <div class="form-actions">
          <button class="btn btn-secondary" id="back-to-form">Back</button>
          <button class="btn btn-primary" id="submit-application">Submit application</button>
        </div>
        <p class="field-error" id="submit-error"></p>
      </section>
    `);

    el.querySelector("#back-to-form").addEventListener("click", () => goTo("form"));
    el.querySelector("#submit-application").addEventListener("click", submitApplication);
    return el;
  }

  async function submitApplication() {
    const btn = document.getElementById("submit-application");
    const errorEl = document.getElementById("submit-error");
    btn.disabled = true;
    btn.textContent = "Submitting…";
    errorEl.textContent = "";

    try {
      const payload = buildPayload();
      const response = await fetch(SUBMIT_ENDPOINT, {
        method: "POST",
        body: payload,
      });
      if (!response.ok) throw new Error(`Submit failed (${response.status})`);
      goTo("submitted");
    } catch (err) {
      errorEl.textContent = "Something went wrong submitting your application. Please try again.";
      btn.disabled = false;
      btn.textContent = "Submit application";
      console.error(err);
    }
  }

  // Builds a multipart/form-data payload so file uploads (proof of
  // ownership, facility photos) travel in the same request as the text
  // fields. The backend (backend/submit-application.js) is what actually
  // turns this into a Shopify metaobject entry — see docs/ARCHITECTURE.md.
  function buildPayload() {
    const form = new FormData();
    form.append("state", state.selectedState);
    form.append("business_type", state.businessType);
    Object.entries(state.fieldValues).forEach(([id, value]) => form.append(id, value));
    Object.entries(state.files).forEach(([id, files]) => {
      (files || []).forEach((file) => form.append(id, file, file.name));
    });
    return form;
  }

  // --- Step 5: submitted ---------------------------------------------

  function renderSubmittedStep() {
    return el_(`
      <section class="step" data-step="submitted">
        <p class="eyebrow">Application received</p>
        <h1>You're in the queue.</h1>
        <p class="lede">A member of the PFS team reviews every application personally. You'll get an email once a decision is made — usually within a few business days. No account is created until you're approved.</p>
      </section>
    `);
  }

  // -----------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------

  function goTo(step) {
    state.step = step;
    render();
    root.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function el_(html) {
    const template = document.createElement("template");
    template.innerHTML = html.trim();
    return template.content.firstElementChild;
  }

  function escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = String(str);
    return div.innerHTML;
  }

  function escapeAttr(str) {
    return String(str).replace(/"/g, "&quot;");
  }

  render();
})();